/**
 * Project Service - API calls for project management
 * Uses axios (same as Assets/Executions) for consistent auth handling
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AD2_REQUEST_DEDUPE = process.env.REACT_APP_AD2_REQUEST_DEDUPE !== 'false';
let projectsRequest = null;

// Legacy: kept for backward compat but no longer used by this service
let getAuthToken = null;
export const setAuthTokenGetter = (getter) => {
  getAuthToken = getter;
};

/**
 * Create a new project
 */
export const createProject = async (projectData) => {
  const response = await axios.post(`${API_URL}/api/projects`, projectData);
  return response.data;
};

/**
 * Get all projects for the current user
 */
export const getProjects = async () => {
  if (AD2_REQUEST_DEDUPE && projectsRequest) return projectsRequest;
  const request = axios.get(`${API_URL}/api/projects`).then((response) => response.data);
  if (!AD2_REQUEST_DEDUPE) return request;
  projectsRequest = request.finally(() => { projectsRequest = null; });
  return projectsRequest;
};

/**
 * Get a specific project by ID
 */
export const getProject = async (projectId) => {
  const response = await axios.get(`${API_URL}/api/projects/${projectId}`);
  return response.data;
};

/**
 * Update a project
 */
export const updateProject = async (projectId, projectData) => {
  const response = await axios.put(`${API_URL}/api/projects/${projectId}`, projectData);
  return response.data;
};
/**
 * Delete a project
 */
export const deleteProject = async (projectId) => {
  const response = await axios.delete(`${API_URL}/api/projects/${projectId}`);
  return response.data;
};

/**
 * Duplicate a project
 */
export const duplicateProject = async (projectId, newName = null) => {
  const url = `${API_URL}/api/projects/${projectId}/duplicate${newName ? `?new_name=${encodeURIComponent(newName)}` : ''}`;
  const response = await axios.post(url);
  return response.data;
};

/**
 * Rename a project
 */
export const renameProject = async (projectId, name, description = null) => {
  const body = { name };
  if (description !== null) body.description = description;
  const response = await axios.patch(`${API_URL}/api/projects/${projectId}`, body);
  return response.data;
};

/**
 * Get execution history for a project
 */
export const getProjectExecutions = async (projectId, limit = 100) => {
  const response = await axios.get(`${API_URL}/api/projects/${projectId}/executions`, { params: { limit } });
  return response.data;
};

/**
 * Bulk-delete projects. If `all` is true, deletes every project for the user.
 * Otherwise only the ones in `projectIds`.
 */
export const bulkDeleteProjects = async ({ projectIds = [], all = false } = {}) => {
  const body = all
    ? { all: true }
    : { project_ids: projectIds };
  const response = await axios.post(`${API_URL}/api/projects/bulk-delete`, body);
  return response.data;
};

/**
 * Templates API — user's saved templates (is_template=true projects).
 */
export const listTemplates = async () => {
  const response = await axios.get(`${API_URL}/api/projects/templates/list`);
  return response.data;
};

export const toggleTemplate = async (projectId) => {
  const response = await axios.post(`${API_URL}/api/projects/${projectId}/toggle-template`);
  return response.data;
};

export const instantiateTemplate = async (payload) => {
  // payload is either {template_id} or {name, nodes, edges, node_id_counter}
  const response = await axios.post(`${API_URL}/api/projects/templates/instantiate`, payload);
  return response.data;
};

// Default export for backward compat
const projectService = {
  createProject,
  getProjects,
  getProject,
  updateProject,
  deleteProject,
  duplicateProject,
  renameProject,
  getProjectExecutions,
  bulkDeleteProjects,
  listTemplates,
  toggleTemplate,
  instantiateTemplate,
};

export default projectService;
